import { z } from 'zod'
import { sessionProfileStorageKeys } from '@/features/session/model/session-profile-storage'

// Shape of GET /admin-backend/api/public/users/context-token/me (`data`).
// Every field is optional-with-fallback so a backend change degrades a card
// instead of breaking the page.
const text = z.string().nullish().catch(null)
const list = <T extends z.ZodType>(item: T) => z.array(item).catch([])

const projectRoleSchema = z.looseObject({
  project_name: text,
  project_role: text,
})

const businessUnitSchema = z.looseObject({
  bu_name: text,
  bu_role: text,
  projects: list(projectRoleSchema),
})

const applicationSchema = z.looseObject({
  id: text,
  application_id: text,
  name: text,
  status: text,
  project: z
    .looseObject({
      name: text,
      business_unit: z.looseObject({ name: text }).nullish().catch(null),
    })
    .nullish()
    .catch(null),
})

const subscriptionSchema = z.looseObject({
  id: text,
  status: text,
  product: z.looseObject({ key: text, name: text }).nullish().catch(null),
  plan: z.looseObject({ name: text, tier: text }).nullish().catch(null),
  period: z
    .looseObject({ starts_at: text, ends_at: text })
    .nullish()
    .catch(null),
  billing: z
    .looseObject({
      cycle: text,
      billable_users: z.number().nullish().catch(null),
      is_per_user: z.boolean().nullish().catch(null),
      unit_price: z.number().nullish().catch(null),
      total_amount: z.number().nullish().catch(null),
    })
    .nullish()
    .catch(null),
  usage: z
    .looseObject({
      quota: z.number().nullish().catch(null),
      used_quota: z.number().nullish().catch(null),
    })
    .nullish()
    .catch(null),
  license: z
    .looseObject({
      number: text,
      deployment_type: text,
      expires_at: text,
      status: text,
      access_allowed: z.boolean().nullish().catch(null),
    })
    .nullish()
    .catch(null),
  tools: list(z.string()),
})

const accountContextSchema = z.looseObject({
  session: z
    .looseObject({ authentication_method: text, expires_at: text })
    .nullish()
    .catch(null),
  user: z
    .looseObject({
      id: text,
      email: text,
      name: text,
      type: text,
      status: text,
      email_verified: z.boolean().nullish().catch(null),
      created_at: text,
    })
    .nullish()
    .catch(null),
  organization: z
    .looseObject({
      key: text,
      name: text,
      status: text,
      account_type: text,
      supported_domains: list(z.string()),
    })
    .nullish()
    .catch(null),
  business_roles: list(z.looseObject({ key: text, name: text })),
  administration: z
    .looseObject({
      is_admin: z.boolean().nullish().catch(null),
      is_org_admin: z.boolean().nullish().catch(null),
      is_super_admin: z.boolean().nullish().catch(null),
    })
    .nullish()
    .catch(null),
  applications: list(applicationSchema),
  business_units: list(businessUnitSchema),
  subscriptions: list(subscriptionSchema),
})

export type AccountContext = z.infer<typeof accountContextSchema>
export type AccountSubscription = z.infer<typeof subscriptionSchema>
export type AccountApplication = z.infer<typeof applicationSchema>
export type AccountBusinessUnit = z.infer<typeof businessUnitSchema>

export function parseAccountContext(payload: unknown): AccountContext | null {
  const envelope = z.looseObject({ data: z.unknown() }).safeParse(payload)
  const candidate = envelope.success ? envelope.data.data : payload
  const parsed = accountContextSchema.safeParse(candidate)
  return parsed.success ? parsed.data : null
}

/** Reads the `/me` response cached by the session check; null if unavailable. */
export function readCachedAccountContext(): AccountContext | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(sessionProfileStorageKeys.profile)
    return raw ? parseAccountContext(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

export interface RoleSummary {
  key: string
  label: string
}

/** The API repeats roles (e.g. QA_TEST_ENGINEER twice); list each key once. */
export function uniqueBusinessRoles(context: AccountContext): RoleSummary[] {
  const seen = new Map<string, RoleSummary>()
  for (const role of context.business_roles) {
    const key = role.key?.trim().toLowerCase()
    if (!key) continue
    const name = role.name?.trim()
    const readable = name && name !== name.toUpperCase() ? name : null
    const existing = seen.get(key)
    if (!existing) seen.set(key, { key, label: readable ?? humanizeKey(key) })
    else if (readable) existing.label = readable
  }
  return [...seen.values()]
}

/** The organization's account type (e.g. "Enterprise"), or `fallback` when the session has none. */
export function accountTypeLabel(
  context: AccountContext | null,
  fallback: string,
): string {
  return humanizeKey(context?.organization?.account_type) || fallback
}

export function humanizeKey(value: string | null | undefined): string {
  const words = (value ?? '')
    .trim()
    .replace(/[_\-/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : ''
}

/** "ForgeSphere - API, MCP & Agentic AI Lifecycle" → name + tagline. */
export function splitProductName(name: string | null | undefined) {
  const [title, ...rest] = (name ?? '').split(' - ')
  return { title: title?.trim() ?? '', tagline: rest.join(' - ').trim() }
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Whole days from `now` until `value`; negative once passed, null if unknown. */
export function daysUntil(
  value: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!value) return null
  const time = new Date(value).getTime()
  if (Number.isNaN(time)) return null
  return Math.ceil((time - now) / 86_400_000)
}
